/**
 * Super-admin dashboard component for DEX UI.
 * Provides tabbed navigation for managing admins and super-admins.
 * Includes party table, details, and modal integrations.
 */

import {
  listAdmins,
  getAdmin,
  createAdmin,
  deleteAdmin,
  listAdminKeys,
  createAdminKey,
  revokeAdminKey,
  listSuperAdmins,
  getSuperAdmin,
  createSuperAdmin,
  deleteSuperAdmin,
  listSuperAdminKeys,
  createSuperAdminKey,
  revokeSuperAdminKey
} from '../../api/super-admin.js';

import {
  setAdminField,
  setAdminStatus,
  selectParty,
  clearSelectedParty,
  setDeleteConfirming,
  clearDeleteConfirming,
  toggleSort,
  setActiveTab,
  getPersistedActiveTab,
  clearAdminState
} from '../../state/admin.js';

import { getState, subscribe } from '../../state/store.js';
import { renderPartyTable, initPartyTable } from './party-table.js';
import { renderPartyDetails, initPartyDetails } from './party-details.js';
import { showCreatePartyModal, hideCreatePartyModal } from './create-party-modal.js';
import { showCreateKeyModal, hideCreateKeyModal } from './create-key-modal.js';
import { showToast } from './toast.js';

// Module-level cleanup functions
let tableCleanup = null;
let detailsCleanup = null;
let partyModalCleanup = null;
let keyModalCleanup = null;
let storeUnsubscribe = null;

/**
 * Render the super-admin dashboard into a container.
 * @param {HTMLElement} container - Container element
 * @param {Object} state - Application state
 */
export function renderSuperAdminDashboard(container, state) {
  const adminState = state.admin || {};
  const session = state.session || {};
  const currentPartyId = session.partyId;

  const {
    admins = [],
    superAdmins = [],
    activeTab = 'admins',
    sortColumn = 'party_id',
    sortDirection = 'asc',
    selectedParty = null,
    selectedPartyKeys = [],
    status = 'idle',
    deleteConfirming = null
  } = adminState;

  // Determine which list to show based on active tab
  const parties = activeTab === 'admins' ? admins : superAdmins;
  const tabTitle = activeTab === 'admins' ? 'Admins' : 'Super Admins';
  const createBtnText = activeTab === 'admins' ? 'Create Admin' : 'Create Super Admin';

  // Build key counts map from loaded keys when party is selected
  const partyKeyCounts = {};
  if (selectedParty && selectedPartyKeys.length >= 0) {
    partyKeyCounts[selectedParty.party_id] = selectedPartyKeys.length;
  }

  // Tab styling based on active state
  const adminsTabClasses = activeTab === 'admins'
    ? 'pb-3 border-b-2 border-blue-600 text-blue-600 font-medium'
    : 'pb-3 border-b-2 border-transparent text-gray-500 hover:text-gray-700';
  const superAdminsTabClasses = activeTab === 'super_admins'
    ? 'pb-3 border-b-2 border-purple-600 text-purple-600 font-medium'
    : 'pb-3 border-b-2 border-transparent text-gray-500 hover:text-gray-700';

  // Determine if we can delete parties (self-protection)
  // For super-admins, also check if this is the last super-admin
  const isLastSuperAdmin = superAdmins.length <= 1;

  container.innerHTML = `
    <div class="super-admin-dashboard">
      <h2 class="text-xl font-semibold text-gray-900 mb-4">Super Admin Dashboard</h2>

      <!-- Tab navigation -->
      <div class="border-b border-gray-200 mb-4">
        <nav class="flex gap-6">
          <button
            type="button"
            data-tab="admins"
            class="${adminsTabClasses}"
          >
            Admins
          </button>
          <button
            type="button"
            data-tab="super_admins"
            class="${superAdminsTabClasses}"
          >
            Super Admins
          </button>
        </nav>
      </div>

      <!-- Tab content header with create button -->
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-gray-700">Manage ${tabTitle}</h3>
        <button
          id="super-admin-create-btn"
          type="button"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          ${status === 'loading' || status === 'creating' ? 'disabled' : ''}
        >
          + ${createBtnText}
        </button>
      </div>

      <!-- Party table -->
      <div id="super-admin-party-table-container"></div>

      <!-- Party details (rendered inside table expansion) -->

      <!-- Modals -->
      <div id="super-admin-create-party-modal-container"></div>
      <div id="super-admin-create-key-modal-container"></div>
    </div>
  `;

  // Render party table
  const tableContainer = container.querySelector('#super-admin-party-table-container');
  if (tableContainer) {
    renderPartyTable(tableContainer, {
      parties,
      partyKeyCounts,
      sortColumn,
      sortDirection,
      expandedPartyId: selectedParty?.party_id || null,
      deleteConfirming,
      loading: status === 'loading',
      currentPartyId,
      isLastSuperAdmin: activeTab === 'super_admins' ? isLastSuperAdmin : false
    });

    // Render party details in expansion slot if expanded
    if (selectedParty) {
      const detailsContainer = tableContainer.querySelector(`#party-details-${selectedParty.party_id}`);
      if (detailsContainer) {
        renderPartyDetails(detailsContainer, {
          party: selectedParty,
          keys: selectedPartyKeys,
          keysLoading: status === 'loading',
          currentPartyId
        });
      }
    }
  }
}

/**
 * Initialize the super-admin dashboard with event handlers and state subscription.
 * @param {HTMLElement} container - Container element
 * @returns {Function} Cleanup function
 */
export function initSuperAdminDashboard(container) {
  const cleanupFunctions = [];

  // Get current state, restore persisted tab if available
  const state = getState();
  const persistedTab = getPersistedActiveTab();
  const activeTab = state.admin?.activeTab || persistedTab;
  const currentPartyId = state.session?.partyId;

  // Ensure state reflects the active tab (for persisted tab recovery)
  if (activeTab !== state.admin?.activeTab) {
    setActiveTab(activeTab);
  }

  // Load initial data based on active tab
  loadParties(activeTab);

  // Wire tab navigation
  function handleTabClick(e) {
    const tabBtn = e.target.closest('[data-tab]');
    if (tabBtn) {
      const newTab = tabBtn.dataset.tab;
      const currentState = getState();
      if (newTab !== currentState.admin?.activeTab) {
        // Clear selection and switch tabs
        clearSelectedParty();
        clearDeleteConfirming();
        setActiveTab(newTab);
        loadParties(newTab);
      }
    }
  }

  container.addEventListener('click', handleTabClick);
  cleanupFunctions.push(() => container.removeEventListener('click', handleTabClick));

  // Wire create button
  function handleCreateClick(e) {
    if (e.target.closest('#super-admin-create-btn')) {
      openCreateModal();
    }
  }

  container.addEventListener('click', handleCreateClick);
  cleanupFunctions.push(() => container.removeEventListener('click', handleCreateClick));

  // Wire party table callbacks
  const tableContainer = container.querySelector('#super-admin-party-table-container');
  if (tableContainer) {
    tableCleanup = initPartyTable(tableContainer, {
      onSort: (column) => {
        toggleSort(column);
      },
      onExpand: async (partyId) => {
        const currentState = getState();
        const currentTab = currentState.admin?.activeTab || 'admins';
        const parties = currentTab === 'admins' ? currentState.admin?.admins : currentState.admin?.superAdmins;
        const party = parties?.find(p => p.party_id === partyId);

        // Toggle expansion
        if (currentState.admin?.selectedParty?.party_id === partyId) {
          clearSelectedParty();
        } else if (party) {
          selectParty(party);
          await loadPartyKeys(partyId, currentTab);
        }
      },
      onDelete: (partyId) => {
        const currentState = getState();
        const currentTab = currentState.admin?.activeTab || 'admins';
        const sessionPartyId = currentState.session?.partyId;

        // Self-protection: cannot delete yourself
        if (partyId === sessionPartyId) {
          showToast('Cannot delete yourself', 'error');
          return;
        }

        // Last super-admin protection
        if (currentTab === 'super_admins') {
          const superAdmins = currentState.admin?.superAdmins || [];
          if (superAdmins.length <= 1) {
            showToast('Cannot delete the last super admin', 'error');
            return;
          }
        }

        setDeleteConfirming(partyId);
      },
      onConfirmDelete: async (partyId) => {
        const currentState = getState();
        const currentTab = currentState.admin?.activeTab || 'admins';

        try {
          setAdminStatus('deleting');

          if (currentTab === 'admins') {
            await deleteAdmin(partyId);
          } else {
            await deleteSuperAdmin(partyId);
          }

          showToast(`${currentTab === 'admins' ? 'Admin' : 'Super admin'} deleted successfully`, 'success');
          clearDeleteConfirming();

          // Reload list
          await loadParties(currentTab);
        } catch (error) {
          const message = error.message || 'Failed to delete';
          if (message.includes('Cannot delete yourself')) {
            showToast('Cannot delete yourself', 'error');
          } else if (message.includes('Cannot delete the last super admin')) {
            showToast('Cannot delete the last super admin', 'error');
          } else {
            showToast(message, 'error');
          }
          setAdminStatus('idle', message);
        }
      },
      onCancelDelete: () => {
        clearDeleteConfirming();
      }
    });
    cleanupFunctions.push(() => tableCleanup && tableCleanup());
  }

  // Subscribe to store changes
  storeUnsubscribe = subscribe((newState, changedPath) => {
    // Re-render on admin state changes
    if (changedPath.includes('admin') || changedPath === '*') {
      renderSuperAdminDashboard(container, newState);

      // Re-initialize table and details after render
      const tableContainer = container.querySelector('#super-admin-party-table-container');
      if (tableContainer) {
        if (tableCleanup) tableCleanup();
        tableCleanup = initPartyTable(tableContainer, {
          onSort: (column) => toggleSort(column),
          onExpand: async (partyId) => {
            const currentState = getState();
            const currentTab = currentState.admin?.activeTab || 'admins';
            const parties = currentTab === 'admins' ? currentState.admin?.admins : currentState.admin?.superAdmins;
            const party = parties?.find(p => p.party_id === partyId);

            if (currentState.admin?.selectedParty?.party_id === partyId) {
              clearSelectedParty();
            } else if (party) {
              selectParty(party);
              await loadPartyKeys(partyId, currentTab);
            }
          },
          onDelete: (partyId) => {
            const currentState = getState();
            const currentTab = currentState.admin?.activeTab || 'admins';
            const sessionPartyId = currentState.session?.partyId;

            if (partyId === sessionPartyId) {
              showToast('Cannot delete yourself', 'error');
              return;
            }

            if (currentTab === 'super_admins') {
              const superAdmins = currentState.admin?.superAdmins || [];
              if (superAdmins.length <= 1) {
                showToast('Cannot delete the last super admin', 'error');
                return;
              }
            }

            setDeleteConfirming(partyId);
          },
          onConfirmDelete: async (partyId) => {
            const currentState = getState();
            const currentTab = currentState.admin?.activeTab || 'admins';

            try {
              setAdminStatus('deleting');

              if (currentTab === 'admins') {
                await deleteAdmin(partyId);
              } else {
                await deleteSuperAdmin(partyId);
              }

              showToast(`${currentTab === 'admins' ? 'Admin' : 'Super admin'} deleted successfully`, 'success');
              clearDeleteConfirming();
              await loadParties(currentTab);
            } catch (error) {
              const message = error.message || 'Failed to delete';
              if (message.includes('Cannot delete yourself')) {
                showToast('Cannot delete yourself', 'error');
              } else if (message.includes('Cannot delete the last super admin')) {
                showToast('Cannot delete the last super admin', 'error');
              } else {
                showToast(message, 'error');
              }
              setAdminStatus('idle', message);
            }
          },
          onCancelDelete: () => clearDeleteConfirming()
        });

        // Re-init party details if expanded
        const selectedParty = newState.admin?.selectedParty;
        if (selectedParty) {
          const detailsContainer = tableContainer.querySelector(`#party-details-${selectedParty.party_id}`);
          if (detailsContainer) {
            if (detailsCleanup) detailsCleanup();
            detailsCleanup = initPartyDetails(detailsContainer, {
              onCreateKey: async (partyId) => {
                await createNewKey(partyId);
              },
              onRevokeKey: async (partyId, keyId) => {
                await revokeKey(partyId, keyId);
              },
              onCopyPublicKey: async (publicKey) => {
                try {
                  await navigator.clipboard.writeText(publicKey);
                  showToast('Public key copied to clipboard', 'success');
                } catch (err) {
                  showToast('Failed to copy public key', 'error');
                }
              }
            });
          }
        }
      }
    }
  });
  cleanupFunctions.push(() => storeUnsubscribe && storeUnsubscribe());

  // Return cleanup function
  return function cleanup() {
    cleanupFunctions.forEach(fn => fn());
    if (tableCleanup) tableCleanup();
    if (detailsCleanup) detailsCleanup();
    if (partyModalCleanup) partyModalCleanup();
    if (keyModalCleanup) keyModalCleanup();
    tableCleanup = null;
    detailsCleanup = null;
    partyModalCleanup = null;
    keyModalCleanup = null;
  };
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Load parties list based on active tab.
 * @param {string} tab - 'admins' or 'super_admins'
 */
async function loadParties(tab) {
  try {
    setAdminStatus('loading');

    if (tab === 'admins') {
      const response = await listAdmins();
      setAdminField('admins', response.admins || []);
    } else {
      const response = await listSuperAdmins();
      setAdminField('superAdmins', response.super_admins || []);
    }

    setAdminStatus('idle');
  } catch (error) {
    const message = error.message || 'Failed to load parties';
    showToast(message, 'error');
    setAdminStatus('idle', message);
  }
}

/**
 * Load API keys for a party.
 * @param {string} partyId - Party ID
 * @param {string} tab - 'admins' or 'super_admins'
 */
async function loadPartyKeys(partyId, tab) {
  try {
    let response;
    if (tab === 'admins') {
      response = await listAdminKeys(partyId);
    } else {
      response = await listSuperAdminKeys(partyId);
    }
    setAdminField('selectedPartyKeys', response.keys || []);
  } catch (error) {
    const message = error.message || 'Failed to load API keys';
    showToast(message, 'error');
    setAdminField('selectedPartyKeys', []);
  }
}

/**
 * Create a new API key for a party.
 * @param {string} partyId - Party ID
 */
async function createNewKey(partyId) {
  const state = getState();
  const currentTab = state.admin?.activeTab || 'admins';
  const selectedParty = state.admin?.selectedParty;

  try {
    let response;
    if (currentTab === 'admins') {
      response = await createAdminKey(partyId);
    } else {
      response = await createSuperAdminKey(partyId);
    }

    // Show API key modal with new key
    const modalContainer = document.querySelector('#super-admin-create-key-modal-container');
    if (modalContainer && response.api_key) {
      keyModalCleanup = showCreateKeyModal(modalContainer, {
        keyId: response.key_id,
        apiKey: response.api_key,
        partyId: partyId,
        role: selectedParty?.role || (currentTab === 'admins' ? 'admin' : 'super_admin')
      }, {
        onClose: () => {
          hideCreateKeyModal(modalContainer);
          keyModalCleanup = null;
          // Reload keys
          loadPartyKeys(partyId, currentTab);
        }
      });
    }

    showToast('API key created successfully', 'success');
  } catch (error) {
    const message = error.message || 'Failed to create API key';
    showToast(message, 'error');
  }
}

/**
 * Revoke an API key.
 * @param {string} partyId - Party ID
 * @param {string} keyId - Key ID
 */
async function revokeKey(partyId, keyId) {
  const state = getState();
  const currentTab = state.admin?.activeTab || 'admins';
  const sessionPartyId = state.session?.partyId;
  const selectedPartyKeys = state.admin?.selectedPartyKeys || [];

  // Self-protection: cannot revoke own last key
  if (partyId === sessionPartyId && selectedPartyKeys.length <= 1) {
    showToast('Cannot revoke your last API key', 'error');
    return;
  }

  // Confirm revocation
  const confirmed = window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.');
  if (!confirmed) return;

  try {
    if (currentTab === 'admins') {
      await revokeAdminKey(partyId, keyId);
    } else {
      await revokeSuperAdminKey(partyId, keyId);
    }

    showToast('API key revoked successfully', 'success');

    // Reload keys
    await loadPartyKeys(partyId, currentTab);
  } catch (error) {
    const message = error.message || 'Failed to revoke API key';
    if (message.includes('Cannot revoke your own last API key')) {
      showToast('Cannot revoke your own last API key', 'error');
    } else {
      showToast(message, 'error');
    }
  }
}

/**
 * Open the create party modal.
 */
function openCreateModal() {
  const state = getState();
  const currentTab = state.admin?.activeTab || 'admins';
  const modalContainer = document.querySelector('#super-admin-create-party-modal-container');

  if (modalContainer) {
    // Role options for super-admin: admin and super_admin
    const roleOptions = ['admin', 'super_admin'];

    partyModalCleanup = showCreatePartyModal(modalContainer, roleOptions, {
      onClose: () => {
        hideCreatePartyModal(modalContainer);
        partyModalCleanup = null;
      },
      onSubmit: async (data) => {
        try {
          setAdminStatus('creating');

          let response;
          if (data.role === 'admin') {
            response = await createAdmin(data.party_id);
          } else {
            response = await createSuperAdmin(data.party_id);
          }

          // Close create party modal
          hideCreatePartyModal(modalContainer);
          partyModalCleanup = null;

          // Show API key modal with new key
          const keyModalContainer = document.querySelector('#super-admin-create-key-modal-container');
          if (keyModalContainer && response.api_key) {
            // Note: Don't call setActiveTab, loadParties, or setAdminStatus here - they trigger
            // re-renders that destroy the modal. Do it in onClose instead.
            const createdRole = data.role;
            keyModalCleanup = showCreateKeyModal(keyModalContainer, {
              keyId: response.key_id,
              apiKey: response.api_key,
              partyId: data.party_id,
              role: data.role
            }, {
              onClose: async () => {
                hideCreateKeyModal(keyModalContainer);
                keyModalCleanup = null;
                // Reload list and reset status after modal closes
                if (createdRole === 'admin') {
                  setActiveTab('admins');
                  await loadParties('admins');
                } else {
                  setActiveTab('super_admins');
                  await loadParties('super_admins');
                }
                setAdminStatus('idle');
              }
            });
          } else {
            // No API key in response - reload list immediately
            if (data.role === 'admin') {
              setActiveTab('admins');
              await loadParties('admins');
            } else {
              setActiveTab('super_admins');
              await loadParties('super_admins');
            }
            setAdminStatus('idle');
          }

          showToast(`${data.role === 'admin' ? 'Admin' : 'Super admin'} created successfully`, 'success');
        } catch (error) {
          const message = error.message || 'Failed to create party';
          showToast(message, 'error');
          setAdminStatus('idle', message);

          // Re-render modal with error
          hideCreatePartyModal(modalContainer);
          partyModalCleanup = null;
        }
      }
    });
  }
}
